/**
 * Compliance Embedded Documents Component
 * Embeds existing Staff Reports components into the Compliance Dashboard
 * Links document uploads to compliance status automatically
 */
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import {
  FileText, Shield, Building2, Flame, AlertTriangle, Truck,
  CheckCircle2, Upload, ExternalLink, ClipboardList, ShieldAlert
} from "lucide-react";

// Import existing Staff Reports components
import {
  TransportationNotificationTab,
  EmergencyDrillLogTab,
  LiabilityInsuranceTab,
  OccupancyPermitTab,
  FireDrillLogTab
} from "@/components/staff-reports";

// Import new compliance forms
import ProviderSurveyForm from "./ProviderSurveyForm";
import ShakenBabyPolicyForm from "./ShakenBabyPolicyForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Map compliance item names to their corresponding components
const COMPLIANCE_COMPONENT_MAP = {
  // Facility Documents
  "Liability Insurance Certificate": {
    component: LiabilityInsuranceTab,
    icon: Shield,
    color: "indigo",
    dataSource: "liability-insurance"
  },
  "Occupancy Permit": {
    component: OccupancyPermitTab,
    icon: Building2,
    color: "teal",
    dataSource: "occupancy-permit"
  },
  "Fire Detection System Documentation": {
    component: FireDrillLogTab,
    icon: Flame,
    color: "orange",
    dataSource: "fire-drills"
  },
  "Provider Survey/Facility Information Sheet": {
    component: ProviderSurveyForm,
    icon: ClipboardList,
    color: "blue",
    dataSource: "provider-surveys"
  },
  
  // Staff Records
  "Shaken Baby Syndrome Policy Acknowledgment": {
    component: ShakenBabyPolicyForm,
    icon: ShieldAlert,
    color: "red",
    dataSource: "shaken-baby-policy-acknowledgments"
  },
  
  // Emergency Preparedness
  "Fire Drill Log": {
    component: FireDrillLogTab,
    icon: Flame,
    color: "orange",
    dataSource: "fire-drills"
  },
  "Emergency Drill Log": {
    component: EmergencyDrillLogTab,
    icon: AlertTriangle,
    color: "orange",
    dataSource: "emergency-drill-logs"
  },
  "Annual Excursion/Transportation Letter": {
    component: TransportationNotificationTab,
    icon: Truck,
    color: "blue",
    dataSource: "transportation-notifications"
  }
};

/**
 * Component to display embedded compliance documents within the dashboard
 */
export const ComplianceEmbeddedDocuments = ({ 
  complianceItem, 
  centerName = "", 
  organization = {},
  onStatusUpdate 
}) => {
  const [hasData, setHasData] = useState(false);
  const [loading, setLoading] = useState(true);

  const componentConfig = COMPLIANCE_COMPONENT_MAP[complianceItem?.name];
  
  useEffect(() => {
    if (componentConfig?.dataSource) {
      checkExistingData();
    } else {
      setLoading(false);
    }
  }, [complianceItem]);

  // Check if there's existing data for this compliance item
  const checkExistingData = async () => {
    try {
      const response = await axios.get(`${API}/${componentConfig.dataSource}`);
      const data = response.data;
      const hasRecords = Array.isArray(data) ? data.length > 0 : !!data;
      setHasData(hasRecords);
      
      // Auto-update compliance status if we have data
      if (hasRecords && complianceItem?.id) {
        await syncComplianceStatus(hasRecords, data);
      }
    } catch (error) {
      console.error("Error checking existing data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Sync compliance status based on existing data
  const syncComplianceStatus = async (hasRecords, data) => {
    if (!complianceItem?.id) return;
    
    try {
      // Determine expiration date from the most recent record
      let expirationDate = null;
      if (Array.isArray(data) && data.length > 0) {
        const mostRecent = data[0]; // Assuming sorted by most recent
        expirationDate = mostRecent.expirationDate || mostRecent.signatureDate;
      }

      const status = hasRecords ? "complete" : "missing";
      
      await axios.put(`${API}/compliance/status/${complianceItem.id}`, {
        status,
        expirationDate,
        notes: `Synced from ${componentConfig.dataSource}`
      });
      
      onStatusUpdate?.();
    } catch (error) {
      // Silent fail - don't interrupt user workflow
      console.error("Error syncing compliance status:", error);
    }
  };

  if (!componentConfig) {
    return null; // No embedded component for this item
  }

  const EmbeddedComponent = componentConfig.component;
  const Icon = componentConfig.icon;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="flex items-center gap-2 mb-4">
        <Icon className={`w-5 h-5 text-${componentConfig.color}-600`} />
        <span className="font-medium text-gray-700">
          {complianceItem.name} - Document Management
        </span>
        {hasData && (
          <Badge className="bg-green-100 text-green-800 border-green-300">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Has Records
          </Badge>
        )}
      </div>
      
      <EmbeddedComponent 
        centerName={centerName} 
        organization={organization}
      />
    </div>
  );
};

/**
 * Panel showing all compliance-linked forms in a tabbed interface
 */
export const ComplianceFormsPanel = ({ 
  category, 
  items = [], 
  centerName = "", 
  organization = {},
  onStatusUpdate 
}) => {
  // Filter items that have embedded components
  const itemsWithComponents = items.filter(item => 
    COMPLIANCE_COMPONENT_MAP[item.name]
  );

  if (itemsWithComponents.length === 0) {
    return null;
  }

  return (
    <Card className="mt-6 border-2 border-dashed border-gray-300">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="w-5 h-5 text-gray-600" />
          Quick Access Forms
        </CardTitle>
        <CardDescription>
          Manage compliance documents directly from the dashboard
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue={itemsWithComponents[0]?.id} className="w-full">
          <TabsList className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4 h-auto">
            {itemsWithComponents.map(item => {
              const config = COMPLIANCE_COMPONENT_MAP[item.name];
              const Icon = config?.icon || FileText;
              
              return (
                <TabsTrigger
                  key={item.id}
                  value={item.id}
                  className="flex items-center gap-2 py-2 px-3 text-xs"
                >
                  <Icon className="w-4 h-4" />
                  <span className="truncate">{item.name.split(' ').slice(0, 2).join(' ')}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {itemsWithComponents.map(item => (
            <TabsContent key={item.id} value={item.id}>
              <ComplianceEmbeddedDocuments
                complianceItem={item}
                centerName={centerName}
                organization={organization}
                onStatusUpdate={onStatusUpdate}
              />
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default ComplianceEmbeddedDocuments;
